import {
  WarningCondition,
  WarningRule,
} from '../../../models/validation-warning.model';

/**
 * AS-842 follow-up — schema-warning detection for the silent-validation path.
 *
 * The mounted editor evaluates each control's schema-declared `warnings`
 * (isEmpty, conditionalRequired, the date checks, valueOutOfRange,
 * patternMismatch, custom, …) via `FieldWarningService` and renders them inline.
 * Until now those warnings drove ONLY the inline field display — they were never
 * rolled up into the sheet-level `hasSuggestedChanges` flag, so a sheet whose
 * only issue was, say, an empty "Date of company listed on Stock Exchange"
 * showed no red asterisk and emitted `hasSuggestedChanges: false` in the
 * submit-for-validation payload.
 *
 * This helper is the DI-free twin of `FieldWarningService.checkWarningCondition`,
 * mirroring `calc-diff.helper.ts` (which is the DI-free twin of the editor's
 * `validateAgainstApiData`). Pure validators call {@link sheetHasTriggeredWarning}
 * to OR "any schema warning triggered on this sheet" into `hasSuggestedChanges`,
 * and the editor's per-sheet status closure calls the SAME function so the red
 * asterisk and the submitted flag always agree.
 *
 * Parity notes vs `FieldWarningService`:
 *  - The `isEditedMode` gate is intentionally NOT replicated. That gate suppresses
 *    inline warnings for brand-new first-time filings; the rolled-up flag is
 *    evaluated unconditionally (a warning is a warning), matching how the
 *    calc-diff path already behaves and guaranteeing editor/payload parity at
 *    submit time (submit always runs on an existing conversion).
 *  - Cross-sheet lookups read `allValues[resolveSheetKey(sheet)]`. Callers build
 *    `allValues` exactly like the editor (`{ ...buildFilingInfoBagEntry(),
 *    ...currentSheetFlatValues }`) — i.e. the active filing-info data is present
 *    under BOTH the `filingInfo` and `filingPrerequisites` alias keys, and the
 *    current sheet's fields sit flat at the top level. A non-filing-info sheet
 *    reference that the bag does not carry resolves to `undefined` and the
 *    warning short-circuits to `false` — identical to the editor, where
 *    `buildFilingInfoBagEntry()` likewise carries only the filing-info slices.
 */

/** Resolve a schema-declared sheet alias to the key present in `allValues`. */
export type SheetKeyResolver = (sheetKey: string) => string;

/**
 * Default resolver: identity, with a filing-info dual-alias fallback. Because
 * callers seed the bag with the active filing-info data under both `filingInfo`
 * and `filingPrerequisites`, identity already hits live data for either alias —
 * the explicit fallback only matters if a future caller seeds just one slot.
 */
const FILING_INFO_ALIASES = ['filingInfo', 'filingPrerequisites'] as const;

function defaultResolveSheetKey(sheetKey: string): string {
  return sheetKey;
}

function isEmptyValue(value: unknown): boolean {
  return value === null || value === undefined || value === '';
}

/** Find a sheet's nested slice in the bag, tolerating the filing-info alias pair. */
function resolveSheetBag(
  allValues: Record<string, any>,
  resolveSheetKey: SheetKeyResolver,
  sheet: string
): Record<string, any> | undefined {
  const resolved = resolveSheetKey(sheet);
  let bag = allValues[resolved];
  if (!bag && (FILING_INFO_ALIASES as readonly string[]).includes(sheet)) {
    for (const alias of FILING_INFO_ALIASES) {
      if (allValues[alias]) {
        bag = allValues[alias];
        break;
      }
    }
  }
  return bag || undefined;
}

/** Read a cross-sheet field, tolerating the filing-info alias pair. */
function readCrossSheetField(
  allValues: Record<string, any>,
  resolveSheetKey: SheetKeyResolver,
  sheet: string,
  field: string
): unknown {
  const bag = resolveSheetBag(allValues, resolveSheetKey, sheet);
  return bag ? bag[field] : undefined;
}

/**
 * Read the value a warning's `conditionalOn` gate tests. The ONE reader both
 * evaluation paths use — FieldWarningService (the inline warning) and
 * {@link checkWarningConditionPure} (the sheet's red asterisk) — so the two
 * cannot disagree about whether a gate is met.
 *
 * Order: the gate sheet's nested slice (with the filing-info alias fallback),
 * else the flat same-sheet field. Both bags carry the current sheet's own
 * fields flat at the top level, which is what makes the fallback meaningful.
 * A gate that names no sheet is a same-sheet gate and reads the flat field.
 */
export function readWarningGateValue(
  allValues: Record<string, any>,
  resolveSheetKey: SheetKeyResolver,
  sheet: string | undefined,
  field: string
): unknown {
  const bag = sheet ? resolveSheetBag(allValues, resolveSheetKey, sheet) : undefined;
  return bag ? bag[field] : allValues[field];
}

function isDateInFuture(date: unknown): boolean {
  if (!date) return false;
  const dateValue = new Date(date as any);
  if (isNaN(dateValue.getTime())) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return dateValue > today;
}

function isDateOlderThan(date: unknown, years: number): boolean {
  if (!date) return false;
  const dateValue = new Date(date as any);
  if (isNaN(dateValue.getTime())) return false;
  const cutoff = new Date();
  cutoff.setFullYear(cutoff.getFullYear() - years);
  return dateValue < cutoff;
}

function checkDateComparison(
  value: unknown,
  operator: 'lte' | 'lt' | 'gte' | 'gt' | 'eq' | 'ne',
  compareWith: { sheet: string; field: string } | undefined,
  allValues: Record<string, any>,
  resolveSheetKey: SheetKeyResolver
): boolean {
  if (!value || !compareWith) return false;
  const compareValue = readCrossSheetField(
    allValues,
    resolveSheetKey,
    compareWith.sheet,
    compareWith.field
  );
  if (!compareValue) return false;

  const currentDate = new Date(value as any);
  const targetDate = new Date(compareValue as any);
  if (isNaN(currentDate.getTime()) || isNaN(targetDate.getTime())) return false;
  currentDate.setHours(0, 0, 0, 0);
  targetDate.setHours(0, 0, 0, 0);
  const current = currentDate.getTime();
  const target = targetDate.getTime();
  switch (operator) {
    case 'lte':
      return current <= target;
    case 'lt':
      return current < target;
    case 'gte':
      return current >= target;
    case 'gt':
      return current > target;
    case 'eq':
      return current === target;
    case 'ne':
      return current !== target;
    default:
      return false;
  }
}

function isOutOfRange(value: unknown, min?: number, max?: number): boolean {
  const num = Number(value);
  if (isNaN(num)) return false;
  if (min !== undefined && num < min) return true;
  if (max !== undefined && num > max) return true;
  return false;
}

function hasPatternMismatch(value: unknown, pattern?: string): boolean {
  if (!pattern || !value) return false;
  try {
    return !new RegExp(pattern).test(String(value));
  } catch {
    return false;
  }
}

function hasInconsistentData(
  value: unknown,
  relatedField: string | undefined,
  allValues: Record<string, any>
): boolean {
  if (!relatedField) return false;
  const relatedValue = allValues[relatedField];
  if (!relatedValue) return false;
  const currentDate = new Date(value as any);
  const relatedDate = new Date(relatedValue as any);
  if (!isNaN(currentDate.getTime()) && !isNaN(relatedDate.getTime())) {
    return currentDate > relatedDate;
  }
  return false;
}

function isDeprecatedValue(value: unknown, deprecatedValues?: string[]): boolean {
  if (!deprecatedValues || deprecatedValues.length === 0) return false;
  return deprecatedValues.includes(String(value));
}

function evaluateCustomCondition(
  value: unknown,
  expression: string,
  allValues: Record<string, any>
): boolean {
  if (!expression) return false;
  try {
    // Mirrors FieldWarningService: available vars are `value` and `allValues`.
    const fn = new Function('value', 'allValues', `return ${expression};`);
    return Boolean(fn(value, allValues));
  } catch {
    return false;
  }
}

/**
 * Pure twin of `FieldWarningService.checkWarningCondition` — returns `true` when
 * a single warning rule's condition is met for `value`.
 */
export function checkWarningConditionPure(
  value: unknown,
  condition: WarningCondition,
  allValues: Record<string, any>,
  resolveSheetKey: SheetKeyResolver = defaultResolveSheetKey
): boolean {
  const empty = isEmptyValue(value);

  if (condition.type === 'isEmpty') {
    return empty;
  }

  if (condition.type === 'conditionalRequired') {
    if (!empty) return false;
    if (condition.dependsOn) {
      const dependentValue = allValues[condition.dependsOn.field];
      return condition.dependsOn.values.includes(dependentValue);
    }
    return false;
  }

  // All other warning types only apply to a populated value.
  if (empty) return false;

  // Conditional gate: skip the warning unless conditionalOn matches.
  if (condition.conditionalOn) {
    const conditionalValue = readWarningGateValue(
      allValues,
      resolveSheetKey,
      condition.conditionalOn.sheet,
      condition.conditionalOn.field
    );
    const expected = condition.conditionalOn.equals;
    if (Array.isArray(expected)) {
      if (!expected.includes(conditionalValue as any)) return false;
    } else if (conditionalValue !== expected) {
      return false;
    }
  }

  switch (condition.type) {
    case 'dateInFuture':
      return isDateInFuture(value);
    case 'dateOlderThanYears':
      return isDateOlderThan(value, condition.years ?? 10);
    case 'dateComparison':
      return checkDateComparison(
        value,
        condition.comparisonOperator ?? 'lte',
        condition.compareWith,
        allValues,
        resolveSheetKey
      );
    case 'valueOutOfRange':
      return isOutOfRange(value, condition.min, condition.max);
    case 'patternMismatch':
      return hasPatternMismatch(value, condition.pattern);
    case 'inconsistentData':
      return hasInconsistentData(value, condition.relatedField, allValues);
    case 'deprecatedValue':
      return isDeprecatedValue(value, condition.deprecatedValues);
    case 'custom':
      return evaluateCustomCondition(value, condition.expression ?? '', allValues);
    default:
      return false;
  }
}

/**
 * Optional cross-sheet inputs for {@link buildWarningBag}. Supplying these lets
 * `dateComparison` (and any `conditionalOn`) warnings whose `compareWith.sheet`
 * is NOT the filing-info sheet resolve in the rollup / headless path, matching
 * what each editor component already hand-wires into its FieldWarningService bag.
 */
export interface WarningBagCrossSheet {
  /**
   * Every sheet slice keyed by its FilingFormState state key, flat by qname
   * (`{ scopeOfFiling: { 'ssmt:...': ... }, auditorsReport: {...}, ... }`).
   * A `compareWith.sheet` ref reads `allValues[sheetKey][field]`, so each
   * referenced sheet must be nested under its key. Accepts the editor's state
   * Record or the pure validators' `context.crossSheetData` Map interchangeably.
   */
  sheets?:
    | ReadonlyMap<string, Record<string, any>>
    | Record<string, Record<string, any>>;
  /**
   * The current sheet's own state key. Nests `sheetData` under it so SAME-sheet
   * `compareWith` refs resolve (e.g. scope's circulation-date-vs-approval-date,
   * where the rule references `sheet: "scopeOfFiling"` — itself).
   */
  sheetKey?: string;
}

/**
 * Build the cross-field bag the warning checks read, in the EXACT shape the
 * editor passes to FieldWarningService: the active filing-info data under both
 * `filingInfo` and `filingPrerequisites` aliases, with the current sheet's own
 * fields flat at the top level. Mirrors the editor's
 * `{ ...currentFormValues, ...buildFilingInfoBagEntry(), <otherSheet>: ... }`.
 * Used by both the pure validators (filing-info pulled from
 * `context.crossSheetData`) and the editor's status closure (filing-info from
 * `getActiveFilingInfoData()`), so the two paths evaluate identical inputs.
 *
 * `crossSheet` is optional for backward compatibility, but MUST be supplied for
 * any sheet whose schema declares a `compareWith.sheet` other than the
 * filing-info alias — otherwise those self / cross-sheet date comparisons
 * silently short-circuit to `false` and never reach `hasSuggestedChanges`
 * (the red asterisk / `Success *`), even though the editor displays them inline.
 */
export function buildWarningBag(
  activeFilingInfo: Record<string, any>,
  sheetData: Record<string, any>,
  crossSheet?: WarningBagCrossSheet
): Record<string, any> {
  // Current sheet's fields flat at top level — for same-sheet `dependsOn` /
  // `relatedField` / `custom` lookups that read `allValues[field]` directly.
  const bag: Record<string, any> = { ...sheetData };

  // Nest every known sheet slice under its state key so cross-sheet
  // `compareWith.sheet` lookups (`allValues[sheetKey][field]`) resolve.
  if (crossSheet?.sheets) {
    const entries =
      crossSheet.sheets instanceof Map
        ? crossSheet.sheets.entries()
        : Object.entries(crossSheet.sheets);
    for (const [key, slice] of entries) {
      if (slice) bag[key] = slice;
    }
  }

  // Nest the current sheet under its own key too, so SAME-sheet date
  // comparisons resolve (and to keep the freshest self data authoritative
  // even if the `sheets` map omits or staled it).
  if (crossSheet?.sheetKey) {
    bag[crossSheet.sheetKey] = sheetData;
  }

  // Active filing-info under BOTH aliases stays authoritative — written last so
  // it overrides any same-keyed slice carried by the `sheets` map.
  bag['filingPrerequisites'] = activeFilingInfo;
  bag['filingInfo'] = activeFilingInfo;

  return bag;
}

/** Minimal shape of a schema control carrying optional warning rules. */
export interface WarnableControl {
  qname: string;
  warnings?: WarningRule[];
}

/**
 * Returns `true` as soon as ANY field on the sheet has a triggered warning.
 *
 * @param controls   The sheet's schema controls (each may declare `warnings`).
 * @param sheetData  The sheet's own values, flat by qname (the value source).
 * @param allValues  The cross-field bag: `{ ...filingInfoAliases, ...sheetData }`
 *                   — same construction the editor passes to FieldWarningService.
 * @param resolveSheetKey Optional alias resolver (editor passes its phase-aware
 *                   one; the pure path relies on the bag carrying both aliases).
 */
export function sheetHasTriggeredWarning(
  controls: ReadonlyArray<WarnableControl>,
  sheetData: Record<string, any>,
  allValues: Record<string, any>,
  resolveSheetKey: SheetKeyResolver = defaultResolveSheetKey
): boolean {
  for (const control of controls) {
    if (!control.warnings || control.warnings.length === 0) continue;
    const value = sheetData[control.qname];
    for (const rule of control.warnings) {
      if (checkWarningConditionPure(value, rule.condition, allValues, resolveSheetKey)) {
        return true;
      }
    }
  }
  return false;
}
